const API_BASE_URL: string = import.meta.env.VITE_API_URL || "";

function withBase(path: string): string {
  return `${API_BASE_URL.replace(/\/$/, "")}${path}`;
}

export interface SchoolSearchParams {
  q?: string;
  gender?: string;
  association?: string;
  division?: string;
  conference?: string;
  region?: string;
  state?: string;
  limit?: number;
  offset?: number;
  groupByCollege?: boolean;
  preferGender?: string;
  min_rank?: number;
  max_rank?: number;
  min_differential?: number;
  max_differential?: number;
  max_acceptance_rate?: number;
  max_net_price?: number;
  major?: string;
  min_grad_rate?: number;
  has_coach_email?: boolean;
  ownership?: string;
  locale_type?: string;
  religious_affiliation?: string;
  saved_team_ids?: string[];
  include_club_golf?: boolean;
}

export interface SchoolResult {
  schoolId: string;
  schoolName: string;
  college_id?: string;
  gender: string;
  association: string | null;
  division: string | null;
  conference: string | null;
  state: string | null;
  city?: string | null;
  region: string | null;
  rank: number | null;
  scoring_avg: number | null;
  headCoach: string | null;
  headCoachEmail: string | null;
  team_url: string | null;
  logoUrl?: string | null;
  brandColorPrimary?: string | null;
  brandColorSecondary?: string | null;
  commit_score_stats?: {
    avg_score_differential?: number | null;
    avg_class_rank?: number | null;
  };
  availableGenders?: string[];
  nccga_club_team?: {
    avg_differential: number | null;
    team_name: string | null;
  } | null;
}

export interface SchoolSearchResponse {
  results: SchoolResult[];
  total: number;
}

export async function fetchSchools(params: SchoolSearchParams): Promise<SchoolSearchResponse> {
  const url = new URL(withBase("/schools/search"));

  if (params.q) url.searchParams.set("q", params.q);
  if (params.gender) url.searchParams.set("gender", params.gender);
  if (params.association) url.searchParams.set("association", params.association);
  if (params.division) url.searchParams.set("division", params.division);
  if (params.conference) url.searchParams.set("conference", params.conference);
  if (params.region) url.searchParams.set("region", params.region);
  if (params.state) url.searchParams.set("state", params.state);
  if (params.limit) url.searchParams.set("limit", String(params.limit));
  if (params.offset != null) url.searchParams.set("offset", String(params.offset));
  if (params.groupByCollege) url.searchParams.set("groupByCollege", "true");
  if (params.preferGender) url.searchParams.set("preferGender", params.preferGender);
  if (params.min_rank != null) url.searchParams.set("min_rank", String(params.min_rank));
  if (params.max_rank != null) url.searchParams.set("max_rank", String(params.max_rank));
  if (params.min_differential != null)
    url.searchParams.set("min_differential", String(params.min_differential));
  if (params.max_differential != null)
    url.searchParams.set("max_differential", String(params.max_differential));
  if (params.max_acceptance_rate != null)
    url.searchParams.set("max_acceptance_rate", String(params.max_acceptance_rate));
  if (params.max_net_price != null)
    url.searchParams.set("max_net_price", String(params.max_net_price));
  if (params.major) url.searchParams.set("major", params.major);
  if (params.min_grad_rate != null)
    url.searchParams.set("min_grad_rate", String(params.min_grad_rate));
  if (params.has_coach_email) url.searchParams.set("has_coach_email", "true");
  if (params.ownership) url.searchParams.set("ownership", params.ownership);
  if (params.locale_type) url.searchParams.set("locale_type", params.locale_type);
  if (params.religious_affiliation)
    url.searchParams.set("religious_affiliation", params.religious_affiliation);
  if (params.saved_team_ids?.length)
    url.searchParams.set("saved_team_ids", params.saved_team_ids.join(","));
  if (params.include_club_golf) url.searchParams.set("include_club_golf", "true");

  const resp = await fetch(url.toString(), {
    method: "GET",
    credentials: "include",
  });

  if (!resp.ok) {
    const t = await resp.text().catch(() => "");
    throw new Error(t || `Failed to search schools (${resp.status})`);
  }

  return resp.json();
}

export interface CollegeDetail {
  college_id: string;
  name: string;
  state: string | null;
  city: string | null;
  school_url: string | null;
  logo_url?: string | null;
  logo_source?: string | null;
  logo_updated_at?: string | null;
  brand_color_primary?: string | null;
  brand_color_secondary?: string | null;
  locale_type: string | null;
  ownership: string | null;
  religious_affiliation: string | null;
  open_admissions: boolean | null;
  admissions: {
    acceptance_rate: number | null;
    sat_scores?: {
      avg_overall?: number | null;
      reading_25th?: number | null;
      reading_75th?: number | null;
      math_25th?: number | null;
      math_75th?: number | null;
    } | null;
    act_scores?: {
      cumulative_25th?: number | null;
      cumulative_75th?: number | null;
    } | null;
  } | null;
  cost: {
    tuition_in_state: number | null;
    tuition_out_of_state: number | null;
    avg_net_price: number | null;
    median_debt: number | null;
    pell_grant_rate: number | null;
  } | null;
  enrollment: {
    total: number | null;
    undergraduate: number | null;
  } | null;
  academics: {
    programs: { cip_code: string; name: string; percentage: number }[];
  } | null;
  outcomes: {
    graduation_rate_4yr: number | null;
    graduation_rate_6yr: number | null;
    retention_rate: number | null;
    median_earnings_10yr: number | null;
  } | null;
}

export async function fetchCollege(collegeId: string): Promise<CollegeDetail> {
  const resp = await fetch(withBase(`/colleges/${encodeURIComponent(collegeId)}`), {
    method: "GET",
    credentials: "include",
  });

  if (!resp.ok) {
    const t = await resp.text().catch(() => "");
    throw new Error(t || `Failed to fetch college (${resp.status})`);
  }

  return resp.json();
}

export async function fetchSchoolById(teamId: string): Promise<SchoolResult> {
  const resp = await fetch(withBase(`/schools/${encodeURIComponent(teamId)}`), {
    method: "GET",
    credentials: "include",
  });

  if (!resp.ok) {
    const t = await resp.text().catch(() => "");
    throw new Error(t || `Failed to fetch school (${resp.status})`);
  }

  return resp.json();
}
